import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, MessageSquare, Sliders, Cpu } from 'lucide-react';
import TopBar from '../components/TopBar';
import PageTitle from '../components/PageTitle';
import PortalHero from '../components/PortalHero';
import SiteFooter from '../components/SiteFooter';
import LlmPlans from './plans/LlmPlans';
import FineTunePlans from './plans/FineTunePlans';
import GpuPlans from './plans/GpuPlans';

type TabKey = 'llm' | 'finetune' | 'gpu';

const TABS: { key: TabKey; path: string; icon: typeof MessageSquare; labelKey: string; fallback: string }[] = [
  { key: 'llm',      path: '/plans',          icon: MessageSquare, labelKey: 'plans.tab_llm',      fallback: 'LLM Subscription' },
  { key: 'finetune', path: '/plans/finetune', icon: Sliders,       labelKey: 'plans.tab_finetune', fallback: 'Fine-Tuning' },
  { key: 'gpu',      path: '/plans/gpu',      icon: Cpu,           labelKey: 'plans.tab_gpu',      fallback: 'GPU Cloud' },
];

function PlansTabs() {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const location = useLocation();

  const activeKey: TabKey =
    location.pathname.startsWith('/plans/finetune') ? 'finetune'
    : location.pathname.startsWith('/plans/gpu')    ? 'gpu'
    : 'llm';

  return (
    <div className="flex justify-center px-4 sm:px-6 lg:px-8 -mt-6 mb-4">
      <div className="inline-flex items-center gap-1 p-1 rounded-xl border border-border bg-card/60 backdrop-blur">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => navigate(tab.path)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(tab.labelKey, tab.fallback)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Plans() {
  const { t } = useTranslation('landing');
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageTitle titleKey="plans.title" namespace="landing" descriptionKey="plans.subtitle" />
      <TopBar />

      <PortalHero
        accent="emerald"
        compact
        badge={<><Sparkles className="h-3.5 w-3.5" />{t('plans.hero_badge')}</>}
        title={t('plans.title')}
        description={t('plans.subtitle')}
      />

      <main className="flex-1">
        <PlansTabs />
        <Routes>
          <Route index element={<LlmPlans />} />
          <Route path="finetune" element={<FineTunePlans />} />
          <Route path="gpu" element={<GpuPlans />} />
          <Route path="*" element={<Navigate to="/plans" replace />} />
        </Routes>
      </main>

      <SiteFooter />
    </div>
  );
}
